import axios from 'axios'
import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'
import {Chart} from 'react-chartjs-2'
import {KTCard, KTCardBody, KTIcon} from '../../../../../_websoft/helpers'
import {proizvodnjaPanelaPoKomercijalistiIzvjestaj} from '../../../../api/izvjestaj'
import LoadingComponent from '../../../../components/LoadingComponent/LoadingComponent'
import TitleComponent from '../../../../components/TitleComponent/TitleComponent'

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Tooltip, Legend)

type CustomerRow = {
  kupac?: string | null
  ukupna_kolicina?: string | number | null
  prethodna_kolicina?: string | number | null
  prije_dvije_godine_kolicina?: string | number | null
  razlika_kolicina?: string | number | null
  promjena_postotak?: string | number | null
  udio_postotak?: string | number | null
}

type CommercialistRow = {
  komercijalista?: string | null
  ime_komercijaliste?: string | null
  komercijalista_ime?: string | null
  ukupna_kolicina?: string | number | null
  prethodna_kolicina?: string | number | null
  prije_dvije_godine_kolicina?: string | number | null
  razlika_kolicina?: string | number | null
  promjena_postotak?: string | number | null
  udio_postotak?: string | number | null
  kupci?: CustomerRow[] | null
}

type ReportDataset = {
  label?: string
  data?: Array<string | number | null>
  backgroundColor?: string | string[]
  borderColor?: string | string[]
  borderWidth?: number
}

type ReportResponse = {
  data?: CommercialistRow[]
  chart?: {
    labels?: Array<string | number>
    datasets?: ReportDataset[]
  }
}

type ReportTab = 'komercijaliste' | 'grafikoni'

const chartColors = ['#1b84ff', '#50cd89', '#f6c000', '#f1416c', '#7239ea']
const numberFormatter = new Intl.NumberFormat('bs-BA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const formatDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatDisplayDate = (date: string) => {
  const [year, month, day] = date.split('-')

  return year && month && day ? `${day}-${month}-${year}` : date
}

const today = new Date()
const defaultDatumOd = `${today.getFullYear()}-01-01`
const defaultDatumDo = formatDate(today)

const toNumber = (value: string | number | null | undefined) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null
  }

  const parsed = Number(typeof value === 'string' ? value.replace(',', '.') : value)

  return Number.isFinite(parsed) ? parsed : null
}

const formatDecimal = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  return parsed === null ? '-' : numberFormatter.format(parsed)
}

const formatPercent = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  return parsed === null ? '-' : `${numberFormatter.format(parsed)}%`
}

const formatDifference = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  if (parsed === null) {
    return '-'
  }

  const sign = parsed > 0 ? '+' : parsed < 0 ? '-' : ''

  return `${sign}${numberFormatter.format(Math.abs(parsed))}`
}

const differenceClass = (value: string | number | null | undefined) => {
  const parsed = toNumber(value)

  if (parsed !== null && parsed > 0) {
    return 'text-success fw-bold'
  }

  if (parsed !== null && parsed < 0) {
    return 'text-danger fw-bold'
  }

  return 'text-gray-700'
}

const getCommercialistName = (row: CommercialistRow) =>
  row.komercijalista?.trim() ||
  row.ime_komercijaliste?.trim() ||
  row.komercijalista_ime?.trim() ||
  'Nije definirano'

const getCustomerName = (row: CustomerRow) => row.kupac?.trim() || 'Nije definirano'

const normalizeResponse = (responseData: any): ReportResponse => {
  if (
    responseData?.data &&
    !Array.isArray(responseData.data) &&
    (responseData.data.data || responseData.data.chart)
  ) {
    return responseData.data
  }

  return responseData ?? {}
}

const getYearLabel = (datumOd: string, datumDo: string, offset = 0) => {
  const godinaOd = Number(datumOd.slice(0, 4))
  const godinaDo = Number(datumDo.slice(0, 4))

  if (!Number.isFinite(godinaOd) || !Number.isFinite(godinaDo)) {
    return ''
  }

  const from = godinaOd + offset
  const to = godinaDo + offset

  return from === to ? String(from) : `${from}-${to}`
}

const IzvjestajPoKomercijalisti: React.FC = () => {
  const [datumOd, setDatumOd] = useState(defaultDatumOd)
  const [datumDo, setDatumDo] = useState(defaultDatumDo)
  const [partner, setPartner] = useState(false)
  const [activeTab, setActiveTab] = useState<ReportTab>('komercijaliste')
  const [report, setReport] = useState<ReportResponse | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => abortControllerRef.current?.abort()
  }, [])

  const handleConfirm = async () => {
    if (!datumOd || !datumDo) {
      setError('Datum od i datum do su obavezni.')
      return
    }

    if (datumOd > datumDo) {
      setError('Datum od ne moze biti veci od datuma do.')
      return
    }

    abortControllerRef.current?.abort()

    const controller = new AbortController()
    abortControllerRef.current = controller
    setLoading(true)
    setError('')
    setExpandedRows(new Set())

    const params: {datumOd: string; datumDo: string; partner?: number} = {datumOd, datumDo}

    if (partner) {
      params.partner = 1
    }

    try {
      const response = await proizvodnjaPanelaPoKomercijalistiIzvjestaj(
        params,
        controller.signal
      )

      setReport(normalizeResponse(response.data))
    } catch (requestError: any) {
      if (axios.isCancel(requestError) || requestError?.code === 'ERR_CANCELED') {
        return
      }

      setReport(null)
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          'Greska prilikom ucitavanja izvjestaja.'
      )
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false)
      }
    }
  }

  const sortedRows = useMemo(
    () =>
      [...(report?.data ?? [])].sort(
        (first, second) =>
          (toNumber(second.ukupna_kolicina) ?? 0) - (toNumber(first.ukupna_kolicina) ?? 0)
      ),
    [report]
  )

  const currentYearLabel = getYearLabel(datumOd, datumDo)
  const previousYearLabel = getYearLabel(datumOd, datumDo, -1)
  const twoYearsAgoYearLabel = getYearLabel(datumOd, datumDo, -2)

  const chartData = useMemo<ChartData<'bar'>>(() => {
    const chart = report?.chart

    return {
      labels: chart?.labels ?? [],
      datasets: (chart?.datasets ?? []).map((dataset, index) => ({
        label: dataset.label || `Serija ${index + 1}`,
        data: (dataset.data ?? []).map((value) => toNumber(value) ?? 0),
        backgroundColor: dataset.backgroundColor || chartColors[index % chartColors.length],
        borderColor: dataset.borderColor || chartColors[index % chartColors.length],
        borderWidth: dataset.borderWidth ?? 1,
      })),
    }
  }, [report])

  const hasData = sortedRows.length > 0
  const hasChartData = (chartData.labels ?? []).length > 0 && chartData.datasets.length > 0

  const chartOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {position: 'top'},
        tooltip: {
          callbacks: {
            title: (items) => items[0]?.label || 'Komercijalista',
            label: (context) =>
              `${context.dataset.label || 'Godina'}: ${formatDecimal(context.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {title: {display: true, text: 'Komercijalista'}},
        y: {
          beginAtZero: true,
          title: {display: true, text: 'Kolicina'},
          ticks: {callback: (value) => formatDecimal(Number(value))},
        },
      },
    }),
    []
  )

  const toggleRow = (rowKey: string) => {
    setExpandedRows((current) => {
      const next = new Set(current)

      if (next.has(rowKey)) {
        next.delete(rowKey)
      } else {
        next.add(rowKey)
      }

      return next
    })
  }

  return (
    <KTCard>
      <div className='card-header border-0'>
        <div className='card-title m-0'>
          <TitleComponent name='Izvjestaj po komercijalisti' />
        </div>
      </div>

      <KTCardBody className='border-top p-9'>
        <div className='row mb-8 align-items-end'>
          <div className='col-lg-3 col-md-6 col-sm-12 mb-6'>
            <label className='form-label fw-bold fs-6'>Datum od</label>
            <input
              type='date'
              name='datumOd'
              className='form-control form-control-lg form-control-solid'
              value={datumOd}
              onChange={(event) => setDatumOd(event.target.value)}
            />
          </div>

          <div className='col-lg-3 col-md-6 col-sm-12 mb-6'>
            <label className='form-label fw-bold fs-6'>Datum do</label>
            <input
              type='date'
              name='datumDo'
              className='form-control form-control-lg form-control-solid'
              value={datumDo}
              onChange={(event) => setDatumDo(event.target.value)}
            />
          </div>

          <div className='col-lg-3 col-md-6 col-sm-12 mb-6'>
            <label className='form-check form-switch form-check-custom form-check-solid mt-3'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={partner}
                onChange={(event) => setPartner(event.target.checked)}
              />
              <span className='form-check-label fw-bold'>Partner komercijalista</span>
            </label>
          </div>

          <div className='col-lg-2 col-md-6 col-sm-12 mb-6'>
            <button
              type='button'
              className='btn btn-primary w-100'
              disabled={loading}
              onClick={handleConfirm}
            >
              {loading ? (
                <>
                  Ucitavanje...
                  <span className='spinner-border spinner-border-sm align-middle ms-2' />
                </>
              ) : (
                <>
                  <KTIcon iconName='check' className='fs-2' />
                  Prikazi
                </>
              )}
            </button>
          </div>
        </div>

        <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x mb-8 fs-6'>
          <li className='nav-item'>
            <button
              type='button'
              className={`nav-link fw-bold ${activeTab === 'komercijaliste' ? 'active' : ''}`}
              onClick={() => setActiveTab('komercijaliste')}
            >
              <KTIcon iconName='people' className='fs-3 me-2' />
              Komercijalisti
            </button>
          </li>
          <li className='nav-item'>
            <button
              type='button'
              className={`nav-link fw-bold ${activeTab === 'grafikoni' ? 'active' : ''}`}
              onClick={() => setActiveTab('grafikoni')}
            >
              <KTIcon iconName='chart-simple' className='fs-3 me-2' />
              Grafikoni
            </button>
          </li>
        </ul>

        {error && (
          <div className='alert alert-danger d-flex align-items-center mb-8' role='alert'>
            <KTIcon iconName='information-2' className='fs-2 me-3' />
            <span className='fw-bold'>{error}</span>
          </div>
        )}

        {loading && <LoadingComponent />}

        {!loading && !error && !report && (
          <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-6'>
            <div className='fw-bold text-gray-700'>Odaberite datume i kliknite Prikazi.</div>
          </div>
        )}

        {!loading && !error && report && !hasData && (
          <div className='notice d-flex bg-light-warning rounded border-warning border border-dashed p-6'>
            <div className='fw-bold text-gray-700'>Nema podataka za odabrani period.</div>
          </div>
        )}

        {!loading && !error && report && hasData && (
          <>
            <div className='notice d-flex align-items-center bg-light-info rounded border-info border border-dashed p-5 mb-8'>
              <KTIcon iconName='calendar-8' className='fs-2 text-info me-3' />
              <div className='fw-bold text-gray-700'>
                Period izvjestaja: {formatDisplayDate(datumOd)} - {formatDisplayDate(datumDo)}
              </div>
            </div>

            {activeTab === 'grafikoni' && (
              <div className='card border border-gray-300 mb-8'>
              <div className='card-header'>
                <h3 className='card-title fw-bolder'>Grafikon po komercijalistima</h3>
              </div>
              <div className='card-body' style={{height: 520}}>
                {hasChartData ? (
                  <Chart type='bar' data={chartData} options={chartOptions} />
                ) : (
                  <div className='h-100 d-flex align-items-center justify-content-center text-muted'>
                    Nema podataka za prikaz grafikona.
                  </div>
                )}
              </div>
              </div>
            )}

            {activeTab === 'komercijaliste' && (
              <div className='card border border-gray-300'>
              <div className='card-header'>
                <h3 className='card-title fw-bolder'>Pregled po komercijalistima</h3>
              </div>
              <div className='card-body p-0'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gy-4 mb-0'>
                    <thead>
                      <tr className='fw-bolder fs-7 text-uppercase bg-dark'>
                        <th className='text-white ps-4'>Komercijalista</th>
                        <th className='text-white text-end'>{currentYearLabel || 'Trenutna kolicina'}</th>
                        <th className='text-white text-end'>{previousYearLabel || 'Prethodna godina'}</th>
                        <th className='text-white text-end'>{twoYearsAgoYearLabel || 'Prije dvije godine'}</th>
                        <th className='text-white text-end'>Razlika</th>
                        <th className='text-white text-end'>Promjena %</th>
                        <th className='text-white text-end pe-4'>Udio %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((row, index) => {
                        const rowKey = `${getCommercialistName(row)}-${index}`
                        const customers = [...(row.kupci ?? [])].sort(
                          (first, second) =>
                            (toNumber(second.ukupna_kolicina) ?? 0) -
                            (toNumber(first.ukupna_kolicina) ?? 0)
                        )
                        const expanded = expandedRows.has(rowKey)

                        return (
                          <React.Fragment key={rowKey}>
                            <tr>
                              <td className='fw-bold text-gray-800 ps-4'>
                                <div className='d-flex align-items-center'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-icon btn-light-primary me-3'
                                    disabled={customers.length === 0}
                                    onClick={() => toggleRow(rowKey)}
                                    aria-label={expanded ? 'Zatvori kupce' : 'Prikazi kupce'}
                                    aria-expanded={expanded}
                                  >
                                    <KTIcon iconName={expanded ? 'minus' : 'plus'} className='fs-4' />
                                  </button>
                                  <span>{getCommercialistName(row)}</span>
                                </div>
                              </td>
                              <td className='text-end'>{formatDecimal(row.ukupna_kolicina)}</td>
                              <td className='text-end'>{formatDecimal(row.prethodna_kolicina)}</td>
                              <td className='text-end'>{formatDecimal(row.prije_dvije_godine_kolicina)}</td>
                              <td className={`text-end ${differenceClass(row.razlika_kolicina)}`}>
                                {formatDifference(row.razlika_kolicina)}
                              </td>
                              <td className={`text-end ${differenceClass(row.promjena_postotak)}`}>
                                {formatPercent(row.promjena_postotak)}
                              </td>
                              <td className='text-end pe-4'>{formatPercent(row.udio_postotak)}</td>
                            </tr>

                            {expanded && (
                              <tr>
                                <td colSpan={7} className='p-0 bg-light'>
                                  <div className='p-5'>
                                    <div className='fw-bolder text-gray-800 mb-4'>
                                      Kupci - {getCommercialistName(row)}
                                    </div>
                                    <div className='table-responsive'>
                                      <table className='table table-sm table-row-dashed align-middle mb-0'>
                                        <thead>
                                          <tr className='fw-bold fs-8 text-uppercase text-muted'>
                                            <th>Kupac</th>
                                            <th className='text-end'>{currentYearLabel || 'Trenutna kolicina'}</th>
                                            <th className='text-end'>{previousYearLabel || 'Prethodna godina'}</th>
                                            <th className='text-end'>{twoYearsAgoYearLabel || 'Prije dvije godine'}</th>
                                            <th className='text-end'>Razlika</th>
                                            <th className='text-end'>Promjena %</th>
                                            <th className='text-end'>Udio %</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {customers.map((customer, customerIndex) => (
                                            <tr key={`${getCustomerName(customer)}-${customerIndex}`}>
                                              <td className='fw-semibold text-gray-700'>{getCustomerName(customer)}</td>
                                              <td className='text-end'>{formatDecimal(customer.ukupna_kolicina)}</td>
                                              <td className='text-end'>{formatDecimal(customer.prethodna_kolicina)}</td>
                                              <td className='text-end'>{formatDecimal(customer.prije_dvije_godine_kolicina)}</td>
                                              <td className={`text-end ${differenceClass(customer.razlika_kolicina)}`}>
                                                {formatDifference(customer.razlika_kolicina)}
                                              </td>
                                              <td className={`text-end ${differenceClass(customer.promjena_postotak)}`}>
                                                {formatPercent(customer.promjena_postotak)}
                                              </td>
                                              <td className='text-end'>{formatPercent(customer.udio_postotak)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            )}
          </>
        )}
      </KTCardBody>
    </KTCard>
  )
}

export default IzvjestajPoKomercijalisti
